import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  catchError,
  defer,
  EMPTY,
  exhaustMap,
  filter,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { PersonService } from './infra/person.service';
import { RegistrationCandidateService } from './infra/registration-candidate.service';
import { RegistrationHealthService } from './infra/registration-health.service';
import { RegistrationPersonService } from './infra/registration-person.service';
import type {
  IRegistrationAttachRequest,
  IRegistrationCandidate,
  IRegistrationCandidateImport,
  IRegistrationCandidateQuery,
  TRegistrationLinkData,
} from './entities/registration-candidate.model';
import type {
  IRegistrationExistingPerson,
  IRegistrationPerson,
  IRegistrationPersonUpdatePlan,
  TRegistrationEmailStatus,
  TRegistrationPersonUpdateChoice,
  TRegistrationTypeName,
} from './entities/registration-person.model';
import {
  buildRegistrationPersonUpdate,
  registrationMainAddress,
  registrationMainMobile,
} from './registration-person-update';

interface IRegistrationCandidateState {
  candidate: IRegistrationCandidate | null;
  /** 404 do Worker: não é falha, é "não há candidata pendente com este CPF". */
  candidateNotFound: boolean;
  /**
   * `true` = livre, `false` = já pertence a alguém, `null` = ainda não sabemos.
   * `null` mantém a confirmação fechada: é o padrão seguro.
   *
   * Written by one of the two questions, never by both for the same
   * candidate. `checkFiscalIdentity` answers `false` for an active customer
   * only; `lookupPerson` answers it for anyone of the gym who has the CPF, and
   * then says who in `existingPerson`. See `personLookupAsked`.
   */
  fiscalIdentityAvailable: boolean | null;
  /**
   * `lookupPerson` was asked about the candidate on screen. From then on the
   * lookup alone says whether the CPF is free, even when it fails. The other
   * question takes a visitor's CPF for a free one, and its answer, arriving
   * after the lookup's, would open the creation of a person who already exists.
   */
  personLookupAsked: boolean;
  /**
   * The person of the gym who already has the CPF of the candidate on screen,
   * as `lookupPerson` found it. `null` with `fiscalIdentityAvailable: true` is
   * "nobody has it"; `null` otherwise is "not known".
   *
   * It is `null` as well, with `fiscalIdentityAvailable: false`, when the
   * lookup found somebody a registration is not attached to: a staff member, a
   * supplier or a helpdesk record.
   */
  existingPerson: IRegistrationExistingPerson | null;
  /**
   * That person as read, with phones and addresses: what the counter compares
   * with the link's data. `null` until the read answers, and when it fails.
   */
  existingPersonRecord: IRegistrationPerson | null;
  /**
   * Whether the link's e-mail may become the e-mail of that person. `null`
   * when it was not asked: the person has an e-mail, or the link has none.
   */
  existingEmailStatus: TRegistrationEmailStatus | null;
  /**
   * The imports still to be finished, each under the id of its candidate. What
   * one of them is: see `unimported`.
   *
   * One for each candidate, and not a single slot. After a failure the counter
   * may go on to another registration, whose chain would take the slot and the
   * marks of the first one with it. Found again, that first registration
   * would be looked up as a new one and its comment written a second time.
   */
  unimportedById: Record<string, IRegistrationCandidateImport>;
  /**
   * The candidate of the import written last: what `unimported` is with
   * another candidate, or none, on screen.
   */
  lastUnimportedId: string | null;
  /**
   * The last import went through with the health answers in the profile comment
   * only: the one attempt at the anamnesis failed. The modal reads it to warn
   * once the profile is open.
   */
  anamnesisFailed: boolean;
  /**
   * The last registration attached to an existing person went through without
   * the link's e-mail, which the check at the counter had let in: it was
   * refused at the write, by the check asked again or by the person service.
   * The modal reads it to say so once the profile is open.
   */
  emailRefused: boolean;
}

const initialState: IRegistrationCandidateState = {
  candidate: null,
  candidateNotFound: false,
  fiscalIdentityAvailable: null,
  personLookupAsked: false,
  existingPerson: null,
  existingPersonRecord: null,
  existingEmailStatus: null,
  unimportedById: {},
  lastUnimportedId: null,
  anamnesisFailed: false,
  emailRefused: false,
};

/** Nobody known for the CPF on screen: where every search and every lookup starts from. */
const noExistingPerson = {
  existingPerson: null,
  existingPersonRecord: null,
  existingEmailStatus: null,
} satisfies Partial<IRegistrationCandidateState>;

/** Keeps the import of one candidate as it is now, beside those of the others. */
const rememberImport =
  (pending: IRegistrationCandidateImport) =>
  (state: IRegistrationCandidateState): Partial<IRegistrationCandidateState> => ({
    unimportedById: { ...state.unimportedById, [pending.candidateId]: pending },
    lastUnimportedId: pending.candidateId,
  });

/** The import of one candidate went through: nothing of it is left to resume. */
const forgetImport =
  (candidateId: string) =>
  (state: IRegistrationCandidateState): Partial<IRegistrationCandidateState> => ({
    unimportedById: Object.fromEntries(
      Object.entries(state.unimportedById).filter(([id]) => id !== candidateId)
    ),
  });

/**
 * Whom a registration is attached to: a customer, a visitor or a contact. The
 * lookup looks at nothing but the CPF, so it also answers a staff member, a
 * supplier and a helpdesk record, and none of those is written on from a link.
 */
const ATTACHABLE_REGISTRATION_TYPES: readonly TRegistrationTypeName[] = [
  'contact',
  'customer',
  'visitor',
];

/** The read answered the person it was asked for. Anything else is never compared nor written on. */
const isRecordOf = (
  record: IRegistrationPerson | null | undefined,
  personId: string
): record is IRegistrationPerson =>
  !!record?.PersonId && record.PersonId.toLowerCase() === personId.toLowerCase();

/** The part of a candidate the person update reads, copied so the retry does not depend on the screen. */
const linkDataOf = (candidate: IRegistrationCandidate): TRegistrationLinkData => ({
  firstName: candidate.firstName,
  lastName: candidate.lastName,
  birthdate: candidate.birthdate,
  ...(candidate.genderType !== undefined ? { genderType: candidate.genderType } : {}),
  email: candidate.email,
  phoneNumber: candidate.phoneNumber,
  phoneCountryCode: candidate.phoneCountryCode,
  address: { ...candidate.address },
});

/**
 * Writes the link's data on a person who already existed, as the attendant
 * chose. Answers the import with the update marked as done, or as it came when
 * there is no update to make.
 *
 * The person is read again here, on every attempt, and the body is built from
 * that read:
 *
 * - The lists replace the stored ones, so they have to be the ones of this
 *   moment. A phone somebody added since the counter's read would be deleted
 *   by a body built from that older read.
 * - A request that failed may have been taken by the service all the same. A
 *   body kept and sent again would insert its new phone and address a second
 *   time; a body built from a fresh read finds them there and adds nothing.
 *
 * The e-mail is asked about again before every body that carries it. The
 * answer frozen at the click may be hours old by a retry, and in between the
 * e-mail may have become somebody's login. Asked again, it can only take the
 * e-mail out, never let it in: the plan the attendant saw is not widened.
 *
 * And it gets one chance. A 400 to a body that carried it is taken as the
 * person service refusing it, which it does for the e-mail of the gym's owner.
 * Either way the person is read once more and what is left is written without
 * the e-mail, which is given up for this registration.
 */
const writePersonUpdate = (
  service: RegistrationPersonService,
  pending: IRegistrationCandidateImport,
  remember: (pending: IRegistrationCandidateImport) => void
): Observable<IRegistrationCandidateImport> => {
  const { attach } = pending;
  if (!attach || attach.choice === 'none' || attach.updateWritten) return of(pending);

  const written = (): IRegistrationCandidateImport => ({
    ...pending,
    attach: { ...attach, updateWritten: true },
  });

  return service.getPersonWithContacts(pending.personId).pipe(
    switchMap((record) => {
      if (!isRecordOf(record, pending.personId)) {
        return throwError(() => new HttpErrorResponse({ status: 404, statusText: 'Not Found' }));
      }

      const { body } = buildRegistrationPersonUpdate(record, attach.link, attach.choice, {
        emailAllowed: attach.emailAllowed,
      });
      if (!body) return of(written());

      const withoutEmail = (): Observable<IRegistrationCandidateImport> => {
        const refused: IRegistrationCandidateImport = {
          ...pending,
          attach: { ...attach, emailAllowed: false, emailRefused: true },
        };
        remember(refused);

        return writePersonUpdate(service, refused, remember);
      };

      const update = service.updatePerson(body).pipe(
        map(() => written()),
        catchError((error: HttpErrorResponse) =>
          body.Email === undefined || error.status !== 400
            ? throwError(() => error)
            : withoutEmail()
        )
      );
      if (body.Email === undefined) return update;

      return service.isEmailUnavailable(body.Email).pipe(
        // As at the counter: anything but a plain `false` leaves the e-mail out.
        catchError(() => of(null)),
        switchMap((unavailable) => (unavailable === false ? update : withoutEmail()))
      );
    })
  );
};

/**
 * Fluxo de balcão da candidata a novo cadastro: buscar pelo CPF, confirmar que
 * o CPF ainda está livre, e — depois do `PersonStore.createPerson` — marcar a
 * candidata como importada no Worker.
 *
 * Store próprio, e não estado no `PersonStore`: este vive o tempo de uma modal
 * e repõe-se a cada abertura; o `PersonStore` é a pessoa em edição.
 *
 * A registration may also come from someone who is ALREADY a person of the
 * gym. `lookupPerson` says so, and `attachToExistingPerson` then ties the
 * registration to that person through the same chain, with no person created.
 * It lives here and not in a store of its own because the two flows share the
 * one retry state: `unimported` is what a reopened modal resumes from, and a
 * second store would have to be told about every step the first one made.
 */
export const RegistrationCandidateStore = signalStore(
  { providedIn: 'root' },
  withState<IRegistrationCandidateState>(initialState),
  withComputed((store) => ({
    /**
     * Pessoa criada cuja candidata o Worker ainda não marcou como importada.
     * Sobrevive ao `resetLookup`: na reabertura o CPF já pertence a essa pessoa,
     * a verificação devolve `false`, e sem isto a candidata ficava presa até ao
     * expurgo dos 90 dias.
     *
     * With health answers it also remembers which of their writes went through,
     * so the retry repeats none of them.
     *
     * The one of the candidate on screen, when that candidate has one. With
     * another candidate on screen, or none, the one written last.
     */
    unimported: computed((): IRegistrationCandidateImport | null => {
      const kept = store.unimportedById();
      const onScreen = store.candidate()?.id;
      const last = store.lastUnimportedId();

      return (onScreen ? kept[onScreen] : null) ?? (last ? kept[last] : null) ?? null;
    }),
  })),
  withComputed((store) => ({
    /**
     * A candidata no ecrã já tem pessoa criada e só falta o import. A modal
     * salta o `createPerson` e a verificação de CPF, e chama só
     * `markImported(unimported())`.
     */
    isImportRetry: computed(() => {
      const candidate = store.candidate();
      return !!candidate && store.unimported()?.candidateId === candidate.id;
    }),
    /**
     * The retry on screen still owes the person the comment with the health
     * answers, so nothing of the questionnaire is on the profile yet. With the
     * comment written, a retry is today's: only the import is missing.
     */
    isHealthCommentRetry: computed(() => {
      const candidate = store.candidate();
      const unimported = store.unimported();
      return (
        !!candidate &&
        unimported?.candidateId === candidate.id &&
        !!unimported.health &&
        !unimported.health.commentWritten
      );
    }),
    /**
     * The retry on screen is of a registration being attached to a person who
     * already existed: no person was created for it. The click resumes it
     * through `attachToExistingPerson`, which hands `unimported()` back to
     * `markImported` with every mark kept. The one thing that click may still
     * change is the choice, to `none`, while the update is not written.
     */
    isAttachRetry: computed(() => {
      const candidate = store.candidate();
      const unimported = store.unimported();
      return !!candidate && unimported?.candidateId === candidate.id && !!unimported.attach;
    }),
    /** The existing person's main mobile, or `null` for a person without one. */
    existingMainMobile: computed(() => {
      const record = store.existingPersonRecord();
      return record ? registrationMainMobile(record) : null;
    }),
    /** The existing person's main address, or `null` for a person without addresses. */
    existingMainAddress: computed(() => {
      const record = store.existingPersonRecord();
      return record ? registrationMainAddress(record) : null;
    }),
    /**
     * What each of the three choices would do to the existing person, for the
     * screen to say before the click. `null` until that person is read.
     *
     * The link's e-mail counts as allowed only once the check answered `free`.
     * While it runs, and when it fails, the e-mail is `refused` here, which is
     * also what a click at that moment writes.
     */
    personUpdatePlans: computed(
      (): Record<TRegistrationPersonUpdateChoice, IRegistrationPersonUpdatePlan> | null => {
        const candidate = store.candidate();
        const record = store.existingPersonRecord();
        if (!candidate || !record) return null;

        const options = { emailAllowed: store.existingEmailStatus() === 'free' };

        return {
          'fill-empty': buildRegistrationPersonUpdate(record, candidate, 'fill-empty', options),
          overwrite: buildRegistrationPersonUpdate(record, candidate, 'overwrite', options),
          none: buildRegistrationPersonUpdate(record, candidate, 'none', options),
        };
      }
    ),
  })),
  withMethods(
    (
      store,
      service = inject(RegistrationCandidateService),
      healthService = inject(RegistrationHealthService),
      personService = inject(PersonService),
      registrationPersonService = inject(RegistrationPersonService),
      errorStore = inject(ErrorStore)
    ) => ({
      /**
       * O 404 é resposta esperada e vai para `candidateNotFound`, não para o
       * estado de erro nem para o toast: a maioria das pessoas que chega ao
       * balcão **não** preencheu o formulário em casa.
       *
       * A verificação de CPF volta a `null`: um `true` deixado pela busca
       * anterior abriria a confirmação desta candidata antes da sua própria
       * verificação.
       *
       * The existing person goes with it, for the same reason: the one found
       * for the CPF before is not the one of this search.
       *
       * Takes the CPF alone, or the CPF with the link picked at the counter.
       * Without a link the Worker looks in the gym's default one.
       */
      findCandidate: rxMethod<string | IRegistrationCandidateQuery>(
        pipe(
          tap(() =>
            patchState(store, {
              candidate: null,
              candidateNotFound: false,
              fiscalIdentityAvailable: null,
              personLookupAsked: false,
              ...noExistingPerson,
              ...setPropInit(
                'checkFiscalIdentity',
                'lookupPerson',
                'readExistingPerson',
                'checkExistingEmail'
              ),
              ...setPropLoading('findCandidate'),
            })
          ),
          // A `null` from an emptied form field is a search for no CPF. Taken
          // apart below as it came, it would throw outside every `tapResponse`
          // and end this method for the life of the store.
          map((query) =>
            typeof query === 'string' ? { fiscalIdentity: query } : query ?? { fiscalIdentity: '' }
          ),
          switchMap(({ fiscalIdentity, linkId }: IRegistrationCandidateQuery) =>
            service.getByFiscalIdentity(fiscalIdentity, linkId).pipe(
              tapResponse({
                next: (candidate) =>
                  patchState(store, { candidate, ...setPropLoaded('findCandidate') }),
                error: (error: HttpErrorResponse) => {
                  if (error.status === 404) {
                    patchState(store, { candidateNotFound: true, ...setPropLoaded('findCandidate') });
                    return;
                  }
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('findCandidate', error) });
                },
              })
            )
          )
        )
      ),

      /**
       * Uma falha deixa `fiscalIdentityAvailable` em `null` e a confirmação
       * fechada — criar uma pessoa por não termos conseguido perguntar daria o
       * cadastro duplicado que esta verificação existe para impedir. O mesmo
       * vale para uma resposta que não seja booleana.
       *
       * Gives way to `lookupPerson`. Once the lookup was asked about the
       * candidate on screen this does nothing, and an answer of its own that
       * was still out is not written: see `personLookupAsked`.
       */
      checkFiscalIdentity: rxMethod<{ fiscalIdentity: string; tenantId: string }>(
        pipe(
          filter(() => !store.personLookupAsked()),
          tap(() =>
            patchState(store, {
              fiscalIdentityAvailable: null,
              ...setPropLoading('checkFiscalIdentity'),
            })
          ),
          switchMap(({ fiscalIdentity, tenantId }) =>
            personService.isFiscalIdentityAvailable(fiscalIdentity, tenantId).pipe(
              tapResponse({
                next: (available) => {
                  // The lookup was asked while this answer was out.
                  if (store.personLookupAsked()) {
                    patchState(store, { ...setPropLoaded('checkFiscalIdentity') });
                    return;
                  }

                  patchState(store, {
                    fiscalIdentityAvailable: typeof available === 'boolean' ? available : null,
                    ...setPropLoaded('checkFiscalIdentity'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('checkFiscalIdentity', error) });
                },
              })
            )
          )
        )
      ),

      /**
       * `unimported` é gravado ANTES da chamada: se o pedido falhar, a nova
       * tentativa reusa o `personId` que já existe em vez de criar outra pessoa.
       *
       * O 409 conta como convergência — a candidata já estava importada, que é
       * exatamente o estado que se queria.
       *
       * Any 409, which is a limit: the Worker that has the trash gives the
       * same status to a registration that is in the trash, and that one it
       * did not mark. Nothing but the text of the message tells the two apart.
       *
       * With health answers the import is the LAST of up to three requests, in
       * this order: the profile comment, the anamnesis, and only then the
       * Worker. Marking is what makes the Worker drop the answers and answer
       * 404 for this CPF, so nothing may be marked before the answers are on
       * the profile. `unimported` is rewritten after each write: a failure
       * further on leaves a retry that writes only what is missing.
       *
       * `exhaustMap`, not `switchMap`: a second call while one is in flight is
       * dropped. Cancelling a write the server may already have taken would
       * leave the state saying it was never made.
       *
       * A registration attached to a person who already existed (`attach`)
       * runs the same chain with two differences. Its health answers never go
       * to the anamnesis: a fresh row of the person's own would outrank the
       * evaluator's anamneses from then on. And between the comment and the
       * import comes the update of that person, when the attendant chose one.
       * The import waits for it: once marked, the Worker would no longer hand
       * the registration out for a retry of the update.
       */
      markImported: rxMethod<IRegistrationCandidateImport>(
        pipe(
          exhaustMap((unimported) => {
            patchState(store, rememberImport(unimported), {
              anamnesisFailed: false,
              emailRefused: false,
              ...setPropLoading('markImported'),
            });

            return of(unimported).pipe(
              // 1. The comment. A failure stops the chain here, with nothing
              //    marked: the modal lands on the retry.
              //    The request is this lib's own. The profile's data lib
              //    imports this one, so going through its service would close
              //    a cycle; and its `ProfileStore.addComment` keeps the failure
              //    to itself and wakes the profile's comment editor.
              switchMap((pending): Observable<IRegistrationCandidateImport> => {
                const { health } = pending;
                if (!health || health.commentWritten) return of(pending);

                return healthService
                  .addProfileComment(pending.personId, health.comment)
                  .pipe(map(() => ({ ...pending, health: { ...health, commentWritten: true } })));
              }),
              tap((pending) => patchState(store, rememberImport(pending))),
              // 2. The anamnesis, once. Marked as sent before the request, so
              //    no retry can post it again; a failure does not stop the
              //    import, it only leaves the answers in the comment alone.
              switchMap((pending): Observable<IRegistrationCandidateImport> => {
                const { health } = pending;
                // Never for a person who already existed, whatever the import
                // was handed: the rule is kept here, not left to the caller.
                if (pending.attach || !health?.anamnesis || health.anamnesisSent) {
                  return of(pending);
                }

                const sent = { ...pending, health: { ...health, anamnesisSent: true } };
                patchState(store, rememberImport(sent));

                return healthService.addOwnerAnamnesis(pending.personId, health.anamnesis).pipe(
                  map(() => sent),
                  catchError(() => of({ ...sent, health: { ...sent.health, anamnesisFailed: true } }))
                );
              }),
              tap((pending) => patchState(store, rememberImport(pending))),
              // 3. The update of a person who already existed, and of nobody
              //    else. A failure stops the chain before the import; once
              //    written it is marked, and no retry touches the person again.
              switchMap((pending) =>
                writePersonUpdate(registrationPersonService, pending, (remembered) =>
                  patchState(store, rememberImport(remembered))
                )
              ),
              tap((pending) => patchState(store, rememberImport(pending))),
              // 4. The import. The 409 is read here and not at the end of the
              //    chain, where a 409 from the comment would pass for it.
              switchMap((pending) =>
                service.markImported(pending.candidateId, pending.personId).pipe(
                  map(() => pending),
                  catchError((error: HttpErrorResponse) =>
                    error.status === 409 ? of(pending) : throwError(() => error)
                  )
                )
              ),
              tapResponse({
                next: ({ candidateId, health, attach }) =>
                  patchState(store, forgetImport(candidateId), {
                    anamnesisFailed: !!health?.anamnesisFailed,
                    emailRefused: !!attach?.emailRefused,
                    ...setPropLoaded('markImported'),
                  }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('markImported', error) });
                },
              })
            );
          })
        )
      ),
    })
  ),
  // The registration of someone who is already a person of the gym: who that
  // person is, and attaching the registration to it. Next to the chain above
  // and not inside it, so the creation of a new person reads as it did.
  withMethods(
    (
      store,
      registrationPersonService = inject(RegistrationPersonService),
      errorStore = inject(ErrorStore)
    ) => {
      /**
       * The answer is still for the candidate on screen. A late answer to the
       * CPF searched before would name the wrong person for this one.
       */
      const isOnScreen = (candidate: IRegistrationCandidate): boolean =>
        store.candidate()?.id === candidate.id;

      /**
       * The read is still of the person named for the candidate on screen. The
       * lookup may have been asked again since, and named nobody or somebody
       * else: a record kept then would be compared, and shown, as that of a
       * person it does not belong to.
       */
      const isStillAsked = (candidate: IRegistrationCandidate, personId: string): boolean =>
        isOnScreen(candidate) &&
        store.existingPerson()?.PersonId?.toLowerCase() === personId.toLowerCase();

      /**
       * Asks whether the link's e-mail is free, when it would be written: the
       * person has none and the link has one. The e-mail asked about is the one
       * the update would carry.
       *
       * A failure raises no alert. The e-mail is then left out, and the screen
       * says so from `existingEmailStatus`.
       */
      const checkEmail = (
        candidate: IRegistrationCandidate,
        record: IRegistrationPerson
      ): Observable<never> => {
        const email = buildRegistrationPersonUpdate(record, candidate, 'fill-empty', {
          emailAllowed: true,
        }).body?.Email;
        if (!email) return EMPTY;

        patchState(store, { ...setPropLoading('checkExistingEmail') });

        return registrationPersonService.isEmailUnavailable(email).pipe(
          filter(() => isOnScreen(candidate)),
          switchMap((unavailable) => {
            // Anything but a plain `false` leaves the e-mail out.
            const existingEmailStatus: TRegistrationEmailStatus =
              unavailable === false ? 'free' : unavailable === true ? 'taken' : 'unchecked';
            patchState(store, { existingEmailStatus, ...setPropLoaded('checkExistingEmail') });

            return EMPTY;
          }),
          catchError((error: HttpErrorResponse) => {
            if (isOnScreen(candidate)) {
              patchState(store, {
                existingEmailStatus: 'unchecked',
                ...setPropError('checkExistingEmail', error),
              });
            }

            return EMPTY;
          })
        );
      };

      /** Reads the existing person with phones and addresses, and then asks about the e-mail. */
      const readPerson = (candidate: IRegistrationCandidate, personId: string): Observable<never> =>
        defer(() => {
          patchState(store, {
            existingPersonRecord: null,
            existingEmailStatus: null,
            ...setPropInit('checkExistingEmail'),
            ...setPropLoading('readExistingPerson'),
          });

          return registrationPersonService.getPersonWithContacts(personId);
        }).pipe(
          filter(() => isStillAsked(candidate, personId)),
          switchMap((record) => {
            // A read that answers nobody, or somebody else, is a read that failed.
            if (!isRecordOf(record, personId)) {
              patchState(store, { ...setPropError('readExistingPerson') });
              return EMPTY;
            }

            patchState(store, {
              existingPersonRecord: record,
              ...setPropLoaded('readExistingPerson'),
            });

            return checkEmail(candidate, record);
          }),
          catchError((error: HttpErrorResponse) => {
            if (isStillAsked(candidate, personId)) {
              errorStore.setHttpError(error);
              patchState(store, { ...setPropError('readExistingPerson', error) });
            }

            return EMPTY;
          })
        );

      return {
        /**
         * Asks the person service who has the CPF of the candidate on screen,
         * and decides "new person or existing one". It replaces
         * `checkFiscalIdentity`, which sees active customers only and names
         * nobody: with it, the CPF of a visitor or of an inactive customer
         * created a second person.
         *
         * - Nobody: `fiscalIdentityAvailable` turns `true`, and a new person may
         *   be created as before.
         * - Somebody: `fiscalIdentityAvailable` turns `false` and
         *   `existingPerson` says who. That person is then read into
         *   `existingPersonRecord`, and the link's e-mail is checked.
         * - Somebody who is not a customer, a visitor or a contact:
         *   `fiscalIdentityAvailable` turns `false` and nobody is named or
         *   read. The CPF is taken, so no new person; and the registration is
         *   not attached to that record. The confirmation stays closed.
         * - A failure, or an answer that is neither, leaves both as they were
         *   reset, with `lookupPersonError` set: the confirmation stays closed.
         *
         * Does nothing on a retry. The person of that candidate is the one in
         * `unimported`: looked up again, a person this counter has just created
         * would come back as someone who already existed.
         */
        lookupPerson: rxMethod<void>(
          pipe(
            map(() => store.candidate()),
            filter(
              (candidate): candidate is IRegistrationCandidate =>
                !!candidate && !store.isImportRetry()
            ),
            tap(() =>
              patchState(store, {
                fiscalIdentityAvailable: null,
                personLookupAsked: true,
                ...noExistingPerson,
                ...setPropInit('readExistingPerson', 'checkExistingEmail'),
                ...setPropLoading('lookupPerson'),
              })
            ),
            switchMap((candidate) =>
              registrationPersonService.findByFiscalIdentity(candidate.fiscalIdentity).pipe(
                filter(() => isOnScreen(candidate)),
                switchMap((answer) => {
                  if (answer?.Outcome === 'not_found') {
                    patchState(store, {
                      fiscalIdentityAvailable: true,
                      ...setPropLoaded('lookupPerson'),
                    });
                    return EMPTY;
                  }

                  const person = answer?.Outcome === 'found' ? answer.Person : null;
                  if (!person?.PersonId) {
                    patchState(store, { ...setPropError('lookupPerson') });
                    return EMPTY;
                  }

                  if (!ATTACHABLE_REGISTRATION_TYPES.includes(person.RegistrationType)) {
                    patchState(store, {
                      fiscalIdentityAvailable: false,
                      ...setPropLoaded('lookupPerson'),
                    });
                    return EMPTY;
                  }

                  patchState(store, {
                    fiscalIdentityAvailable: false,
                    existingPerson: {
                      PersonId: person.PersonId,
                      RegistrationType: person.RegistrationType,
                      DisplayName: person.DisplayName ?? null,
                    },
                    ...setPropLoaded('lookupPerson'),
                  });

                  return readPerson(candidate, person.PersonId);
                }),
                catchError((error: HttpErrorResponse) => {
                  if (isOnScreen(candidate)) {
                    errorStore.setHttpError(error);
                    patchState(store, { ...setPropError('lookupPerson', error) });
                  }

                  return EMPTY;
                })
              )
            )
          )
        ),

        /** Reads the existing person again, after a read that failed. */
        readExistingPerson: rxMethod<void>(
          pipe(
            switchMap(() => {
              const candidate = store.candidate();
              const existing = store.existingPerson();

              return candidate && existing ? readPerson(candidate, existing.PersonId) : EMPTY;
            })
          )
        ),

        /**
         * Attaches the registration on screen to the person who already has its
         * CPF, updated as the attendant chose, and marks it as handled. No
         * person is created.
         *
         * Runs the chain of `markImported`, with the person's id: the comment
         * (when the screen hands one), the update (when the choice writes),
         * the import. What the chain needs is frozen here, at the click.
         *
         * - With a retry on screen it resumes that retry, with every mark kept:
         *   a chain started over would write the comment a second time. Of what
         *   is asked, only `none` is heard, and only while the update is not
         *   written: it is the way out of an update that keeps failing, and the
         *   registration is then marked with the person left as it is. Any
         *   other choice resumes with the one of the first click.
         * - A choice that writes needs the person as read. Without it only
         *   `none` goes through, and the screen is to offer nothing else.
         * - The link's e-mail goes only if the check answered `free` by now.
         */
        attachToExistingPerson: ({ choice, healthComment }: IRegistrationAttachRequest): void => {
          const unimported = store.unimported();
          if (store.isImportRetry() && unimported) {
            const { attach } = unimported;

            store.markImported(
              attach && !attach.updateWritten && choice === 'none'
                ? {
                    ...unimported,
                    // Nothing is written on the person from here on, so no
                    // e-mail was left out of anything: an earlier attempt's
                    // refusal is not to be reported at the end.
                    attach: {
                      ...attach,
                      choice,
                      ...(attach.emailRefused ? { emailRefused: false } : {}),
                    },
                  }
                : unimported
            );
            return;
          }

          const candidate = store.candidate();
          const existing = store.existingPerson();
          if (!candidate || !existing) return;
          if (choice !== 'none' && !store.existingPersonRecord()) return;

          store.markImported({
            candidateId: candidate.id,
            personId: existing.PersonId,
            ...(healthComment ? { health: { comment: healthComment, anamnesis: null } } : {}),
            attach: {
              choice,
              link: linkDataOf(candidate),
              emailAllowed: store.existingEmailStatus() === 'free',
            },
          });
        },
      };
    }
  ),
  withReqState(
    'findCandidate',
    'checkFiscalIdentity',
    'markImported',
    'lookupPerson',
    'readExistingPerson',
    'checkExistingEmail'
  ),
  // Below the request states, which it reads: the methods above are put
  // together before them and do not see them.
  withMethods((store) => ({
    /**
     * Chamado ao abrir a modal. NÃO limpa `unimported` — ver o estado.
     *
     * Nor does it take `markImported` out of `loading`. The chain belongs to
     * this store and outlives the modal that started it. With its state back
     * at `init`, a modal opened again would let another registration be
     * confirmed while it still runs: that call is dropped, and the end of the
     * chain still running would then be taken for the end of the dropped one.
     */
    resetLookup: (): void =>
      patchState(
        store,
        {
          candidate: null,
          candidateNotFound: false,
          fiscalIdentityAvailable: null,
          personLookupAsked: false,
          ...noExistingPerson,
          anamnesisFailed: false,
          emailRefused: false,
        },
        {
          ...setPropInit(
            'findCandidate',
            'checkFiscalIdentity',
            'lookupPerson',
            'readExistingPerson',
            'checkExistingEmail'
          ),
        },
        store.markImportedLoading() ? {} : { ...setPropInit('markImported') }
      ),
  })),
  withComputed((store) => ({
    /**
     * The question "who has this CPF" is still being answered: the lookup, the
     * read of the person found, or the check of the e-mail. Until it turns
     * `false` the choices are not to be offered.
     */
    personLookupRunning: computed<boolean>(
      () =>
        !!store.lookupPersonLoading() ||
        !!store.readExistingPersonLoading() ||
        !!store.checkExistingEmailLoading()
    ),
  }))
);
